(() => {
  const REGION_CONTENT = {
    CN: {
      message: 'Shopping from Mainland China? Visit our official China website for local purchasing options.',
      buttonLabel: 'Visit China Website',
      destinationKey: 'chinaUrl',
    },
    HK: {
      message: 'Shopping from Hong Kong? Visit our official Hong Kong website for local purchasing options.',
      buttonLabel: 'Visit Hong Kong Website',
      destinationKey: 'hongKongUrl',
    },
    INTERNATIONAL: {
      message: 'Shopping outside the US? BioEmblem products are available on iHerb.',
      buttonLabel: 'Shop on iHerb',
      destinationKey: 'iherbUrl',
    },
  };

  class RegionRecommendation extends HTMLElement {
    constructor() {
      super();
      this.abortController = null;
      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
    }

    connectedCallback() {
      if (this.dataset.initialized === 'true') return;

      // The regional recommendation belongs to the standard storefront experience.
      // If a campaign/landing page removes or hides the main navigation, remove the
      // recommendation too so the page remains focused and interruption-free.

      if (!this.hasVisibleMainNavigation()) {
        this.remove();
        return;
      }

      this.messageElement = this.querySelector('[data-region-message]');
      this.visitLink = this.querySelector('[data-region-visit]');
      this.closeButtons = this.querySelectorAll('[data-region-close]');
      this.regionSelectors = document.querySelectorAll('[data-region-selector]');
      this.regionOptions = document.querySelectorAll('[data-region-option]');

      this.regionSelectors.forEach((selector) => {
        const toggle = selector.querySelector('[data-region-toggle]');
        if (!toggle) return;

        toggle.addEventListener('click', () => {
          if (!window.matchMedia('(max-width: 749px)').matches) return;

          const isOpen = selector.classList.toggle('is-open');
          toggle.setAttribute('aria-expanded', String(isOpen));
        });
      });

      if (!this.messageElement || !this.visitLink || !this.closeButtons.length) return;

      this.addEventListener('click', this.handleClick);
      this.dataset.initialized = 'true';

      const localTestCountry = this.getLocalTestCountry();
      if (localTestCountry) {
        this.applyDetectedCountry(localTestCountry, true);
        return;
      }

      this.detectCountry();
    }

    hasVisibleMainNavigation() {
      const mainNavigation = document.querySelector('.section-header');
      if (!mainNavigation) return false;

      const navigationStyles = window.getComputedStyle(mainNavigation);
      return (
        mainNavigation.getClientRects().length > 0 &&
        navigationStyles.display !== 'none' &&
        navigationStyles.visibility !== 'hidden'
      );
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.removeEventListener('click', this.handleClick);
      document.removeEventListener('keydown', this.handleKeydown);
    }

    get storageDays() {
      const days = Number.parseInt(this.dataset.storageDays, 10);
      return Number.isFinite(days) && days > 0 ? days : 90;
    }

    getLocalTestCountry() {
      const localHosts = ['127.0.0.1', 'localhost', '[::1]'];
      if (!localHosts.includes(window.location.hostname)) return null;

      return new URLSearchParams(window.location.search).get('region_test')?.toUpperCase() || null;
    }

    getPreference() {
      try {
        const rawValue = window.localStorage.getItem(this.dataset.storageKey);
        if (!rawValue) return null;

        const saved = JSON.parse(rawValue);
        if (!saved.region || !saved.expiresAt || Date.now() >= saved.expiresAt) {
          window.localStorage.removeItem(this.dataset.storageKey);
          return null;
        }

        return saved.region;
      } catch (error) {
        return null;
      }
    }

    remember(region) {
      if (!region) return;

      try {
        window.localStorage.setItem(
          this.dataset.storageKey,
          JSON.stringify({
            region,
            expiresAt: Date.now() + this.storageDays * 24 * 60 * 60 * 1000,
          })
        );
      } catch (error) {
        // Storage may be unavailable in privacy-restricted browsers.
      }
    }

    async detectCountry() {
      const localHosts = ['127.0.0.1', 'localhost', '[::1]'];
      if (localHosts.includes(window.location.hostname)) return;

      this.abortController = new AbortController();
      const timeoutId = window.setTimeout(() => this.abortController.abort(), 8000);
      const root = window.Shopify?.routes?.root || '/';
      const endpoint = `${root}browsing_context_suggestions.json?country%5Benabled%5D=true`;

      try {
        const response = await window.fetch(endpoint, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          signal: this.abortController.signal,
        });
        if (!response.ok) return;

        const data = await response.json();
        const country = data?.detected_values?.country?.handle?.toUpperCase();
        this.applyDetectedCountry(country);
      } catch (error) {
        // Region detection is an enhancement and must never block the storefront.
      } finally {
        window.clearTimeout(timeoutId);
      }
    }

    applyDetectedCountry(country, forceOpen = false) {
      if (!country) return;

      this.activeRegion = country === 'CN' || country === 'HK' ? country : 'INTERNATIONAL';
      this.regionSelectors.forEach((selector) => {
        selector.hidden = false;
      });
      this.regionOptions.forEach((option) => {
        option.hidden = option.dataset.regionOption !== this.activeRegion;
      });

      // US visitors can reopen the iHerb information from the globe menu, but
      // they should not receive an automatic international recommendation bar.
      if (country === 'US') return;

      if (forceOpen || !this.getPreference()) this.open(this.activeRegion);
    }

    open(region = this.activeRegion) {
      const content = REGION_CONTENT[region];
      const destination = content ? this.dataset[content.destinationKey] : null;
      if (!content || !destination) return;

      this.activeRegion = region;
      this.messageElement.textContent = content.message;
      this.visitLink.textContent = content.buttonLabel;
      this.visitLink.href = destination;
      this.hidden = false;
      document.documentElement.classList.add('region-recommendation-open');
      document.addEventListener('keydown', this.handleKeydown);
    }

    closeAndRemember() {
      this.remember(this.activeRegion);
      this.close();
    }

    close() {
      this.hidden = true;
      document.documentElement.classList.remove('region-recommendation-open');
      document.removeEventListener('keydown', this.handleKeydown);
    }

    handleClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      if (target.closest('[data-region-close]')) {
        this.closeAndRemember();
        return;
      }

      if (target.closest('[data-region-visit]')) {
        this.remember(this.activeRegion);
        this.close();
      }
    }

    handleKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeAndRemember();
      }
    }
  }

  if (!customElements.get('region-recommendation')) {
    customElements.define('region-recommendation', RegionRecommendation);
  }
})();
